import React, { useState, useEffect } from 'react';
import {
  Card,
  Typography,
  Space,
  Button,
  Input,
  message,
  Table,
  Tag,
  Breadcrumb,
  Spin,
  Row,
  Col,
  Divider,
  Alert,
  Tooltip,
} from 'antd';
import {
  GiftOutlined,
  CopyOutlined,
  ShareAltOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  UserAddOutlined,
  TrophyOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { referralApi } from '../../api';
import { ExpProgress } from './RankComponents';

const { Title, Text, Paragraph } = Typography;

/**
 * 紹介システムページ
 */
const ReferralPage = () => {
  const [loading, setLoading] = useState(true);
  const [info, setInfo] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [infoData, statsData] = await Promise.all([
        referralApi.getInfo(),
        referralApi.getStats(),
      ]);
      setInfo(infoData);
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load referral data:', error);
      // アラートは表示せず、ボックス表示のみ
    } finally {
      setLoading(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(info.referral_url);
      message.success('紹介URLをコピーしました！');
    } catch (error) {
      message.error('コピーに失敗しました');
    }
  };

  const handleShareTwitter = () => {
    const text = `magicssで記事作成を効率化！\n紹介URL: ${info.referral_url}`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="aio-dashboard">
        <Breadcrumb
          style={{ marginBottom: 16 }}
          items={[
            { title: <Link to="/mypage">マイページ</Link> },
            { title: '友達紹介' },
          ]}
        />
        <Alert
          message="紹介機能はまだ利用できません"
          description={
            <div>
              <Paragraph style={{ marginBottom: 16 }}>
                紹介機能は <Text strong>ブロンズ III ランク（50 EXP）</Text>{' '}
                以上で利用可能になります。
              </Paragraph>
              <Button type="primary">
                <Link to="/rank-system" style={{ color: 'white' }}>
                  ランクシステムを見る
                </Link>
              </Button>
            </div>
          }
          type="warning"
          showIcon
        />
      </div>
    );
  }

  const columns = [
    {
      title: '紹介ユーザー',
      dataIndex: 'referred_user_email',
      key: 'email',
      render: email => <Text>{email}</Text>,
    },
    {
      title: '登録日',
      dataIndex: 'referred_at',
      key: 'referred_at',
      render: date => new Date(date).toLocaleDateString('ja-JP'),
    },
    {
      title: '現在のEXP',
      dataIndex: 'current_exp',
      key: 'current_exp',
      render: exp => (
        <div style={{ minWidth: 120 }}>
          <ExpProgress totalExp={exp} showNumbers={false} size="small" />
        </div>
      ),
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        if (status === 'completed') {
          return (
            <Tag icon={<CheckCircleOutlined />} color="success">
              完了
            </Tag>
          );
        }
        return (
          <Tooltip title={`${info.completion_requirement}`}>
            <Tag icon={<ClockCircleOutlined />} color="processing">
              進行中 ({record.current_exp} / 25 EXP)
            </Tag>
          </Tooltip>
        );
      },
    },
    {
      title: '報酬',
      dataIndex: 'reward_given',
      key: 'reward',
      render: (given, record) =>
        given ? (
          <Tag color="purple">
            +{record.reward_amount || info.referral_reward_amount} クレジット
          </Tag>
        ) : (
          <Text type="secondary">-</Text>
        ),
    },
  ];

  return (
    <div className="aio-dashboard">
      {/* パンくずリスト */}
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[
          { title: <Link to="/mypage">マイページ</Link> },
          { title: '友達紹介' },
        ]}
      />

      {/* ページヘッダー */}
      <div style={{ marginBottom: 24 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title
              level={1}
              className="gradient-text"
              style={{ fontSize: 24, marginBottom: 8 }}
            >
              友達紹介
            </Title>
            <Text type="secondary">
              友達を紹介して、お互いに {info.referral_reward_amount}{' '}
              クレジットをゲット！
            </Text>
          </Col>
        </Row>
      </div>

      {/* 統計サマリー */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card className="stat-card" style={{ boxShadow: 'none', border: '1px solid #f0f0f0' }}>
            <div style={{ textAlign: 'center' }}>
              <UserAddOutlined
                style={{
                  fontSize: 24,
                  color: '#1890ff',
                  marginBottom: 8,
                  marginRight: 0,
                }}
              />
              <div style={{ fontSize: 24, fontWeight: 600 }}>
                {info.total_referrals}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                総紹介数
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card" style={{ boxShadow: 'none', border: '1px solid #f0f0f0' }}>
            <div style={{ textAlign: 'center' }}>
              <ClockCircleOutlined
                style={{
                  fontSize: 24,
                  color: '#1890ff',
                  marginBottom: 8,
                  marginRight: 0,
                }}
              />
              <div style={{ fontSize: 24, fontWeight: 600, color: '#1890ff' }}>
                {info.pending_referrals}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                進行中
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card" style={{ boxShadow: 'none', border: '1px solid #f0f0f0' }}>
            <div style={{ textAlign: 'center' }}>
              <CheckCircleOutlined
                style={{
                  fontSize: 24,
                  color: '#52c41a',
                  marginBottom: 8,
                  marginRight: 0,
                }}
              />
              <div style={{ fontSize: 24, fontWeight: 600, color: '#52c41a' }}>
                {info.completed_referrals}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                完了
              </Text>
            </div>
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card className="stat-card" style={{ boxShadow: 'none', border: '1px solid #f0f0f0' }}>
            <div style={{ textAlign: 'center' }}>
              <TrophyOutlined
                style={{
                  fontSize: 24,
                  color: '#722ed1',
                  marginBottom: 8,
                  marginRight: 0,
                }}
              />
              <div style={{ fontSize: 24, fontWeight: 600, color: '#722ed1' }}>
                {info.total_rewards}
              </div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                獲得クレジット
              </Text>
            </div>
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]}>
        {/* 紹介コード・URL */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <ShareAltOutlined />
                <span>あなたの紹介URL</span>
              </Space>
            }
            style={{ height: '100%' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              {/* 紹介URL */}
              <div>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
                >
                  このURLを友達にシェアしてください
                </Text>
                <Space.Compact style={{ width: '100%' }}>
                  <Input value={info.referral_url} readOnly />
                  <Button
                    type="primary"
                    icon={<CopyOutlined />}
                    onClick={handleCopyUrl}
                  >
                    コピー
                  </Button>
                </Space.Compact>
              </div>

              <Divider style={{ margin: '12px 0' }} />

              {/* SNSシェアボタン */}
              <div>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 8, fontSize: 12 }}
                >
                  SNSでシェア
                </Text>
                <Button
                  type="default"
                  icon={<ShareAltOutlined />}
                  onClick={handleShareTwitter}
                  style={{
                    background: '#000',
                    color: 'white',
                    borderColor: '#000',
                  }}
                >
                  Xでシェア
                </Button>
              </div>
            </Space>
          </Card>
        </Col>

        {/* 紹介の流れ */}
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <GiftOutlined />
                <span>紹介の流れ</span>
              </Space>
            }
            style={{ height: '100%' }}
          >
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <div
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#1890ff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  1
                </div>
                <div>
                  <Text strong>紹介URLをシェア</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    上記のURLを友達に送ってください
                  </Text>
                </div>
              </div>

              <div
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#1890ff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  2
                </div>
                <div>
                  <Text strong>友達が新規登録</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    紹介URLから登録すると自動的に紐付けされます
                  </Text>
                </div>
              </div>

              <div
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#1890ff',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  3
                </div>
                <div>
                  <Text strong>友達がブロンズ I に到達</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    25 EXP獲得でブロンズ I に到達
                  </Text>
                </div>
              </div>

              <div
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}
              >
                <div
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: '#52c41a',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}
                >
                  ✓
                </div>
                <div>
                  <Text strong style={{ color: '#52c41a' }}>
                    双方に {info.referral_reward_amount} クレジット付与！
                  </Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    あなたと友達の両方にボーナスが付与されます
                  </Text>
                </div>
              </div>
            </Space>
          </Card>
        </Col>
      </Row>

      {/* 紹介履歴 */}
      <Card
        title={
          <Space>
            <UserAddOutlined />
            <span>紹介履歴</span>
          </Space>
        }
        style={{ marginTop: 16 }}
      >
        {stats && stats.referrals.length > 0 ? (
          <Table
            dataSource={stats.referrals}
            columns={columns}
            pagination={{ pageSize: 10 }}
            rowKey={(record, index) => index}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <Paragraph type="secondary">まだ紹介履歴がありません</Paragraph>
            <Paragraph type="secondary">
              友達を紹介して、お互いに {info.referral_reward_amount}{' '}
              クレジットを獲得しましょう！
            </Paragraph>
          </div>
        )}
      </Card>
    </div>
  );
};

export default ReferralPage;
